
import "./App.css";
import 'react-multi-carousel/lib/styles.css';
import Home from "./Home/Home";
import Scraper from "./Admin/Scraper";
import { CssBaseline, Toolbar, Typography } from "@mui/material";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import { cloneElement, useContext, useEffect } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import "animate.css";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
} from "react-router-dom";
import Campaigns from "./Campaigns/Campaigns";
import Campaign from "./Campaign/Campaign";
import Workstream from "./Workstream/Workstream";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ProductRedirect } from "./ProductRedirect/ProductRedirect";
import Pricing from "./Pricing/Pricing";
import Login from "./Login/Login";
import Register from "./Register/Register";
import Setup from "./Setup/Setup";
import Lottie from "react-lottie";
import * as animationData from "./assets/ai-animation.json";
import LoadingContext from "./contexts/LoadingContext";
import { TypeAnimation } from "react-type-animation";
import Privacy from "./Privacy";
import Terms from "./Terms";
import TiktokOauth from "./Oauth/Tiktok";
import YoutubeOAuth from "./Oauth/Youtube";
import UserContext from "./contexts/UserContext";
import { logout } from "./util/helpers";
import FacebookOauth from "./Oauth/Facebook";
import Unsubscribe from "./Mailing/Unsubscribe";
import BlogArticle from "./Blog/Blog";
import BlogArticle3 from "./Blog/BlogArticle3";
import BlogArticle4 from "./Blog/BlogArticle4";
import BlogArticle2 from "./Blog/BlogArticle2";
import PrivacyPolicy from "./Blog/PrivacyPolicy";
import CreatorHome from "./CreatorJourney/CreatorHome";
import CreatorSignUp from "./Register/CreatorSignUp";
import FrequentlyAskedQuestions from "./Campaigns/FAQ";
import AnalyticsDashboard from "./Campaigns/AnalyticsDashboard";
import SocialTrends from "./Campaigns/SocialTrends";
import CreatorDiscovery from "./Campaigns/CreatorDiscovery";
import MyAccount from "./Campaigns/MyAccount";
import LearnMoreOutreach from "./Campaign/LearnMoreOutreach";
import AutoInviteLogin from "./AutoInvite/AutoInviteLogin";
import AutoInviteRegister from "./AutoInvite/AutoInviteRegister";
import CreatorMyAccount from "./Creators/CreatorMyAccount";


import LimitedUse from "./LimitedUse";
import AccountDeletion from "./AccountDeletion";
import FAQ from "./FAQ";
import TempAddAnalytics from "./TempAddAnalytics";
import { AppBlockingRounded } from "@mui/icons-material";
import CreatorLogin from "./Login/CreatorLogin";
import Creators from "./Creators/Creators";
import CreatorsWorkstream from "./Creators/CreatorsWorkstream";
import Landscape from "./Competitive/Landscape";
import CreatorSetup from "./CreatorSetup/CreatorSetup";
import Favorites from "./Favorites/Favorites";
import { Layout, ScrollToTop } from "./components";
import { RouterErrorFallback } from "./components/ErrorBoundary";
import Dashboard from "./Dashboard/Dashboard";
import NewCampaignPage from "./Campaign/NewCampaignPage";
import CreateCampaign from "./Campaign/NewCreateCampaign";
import BlogArticle5 from "./Blog/BlogArticle5";
import CreatorCampaignPage from "./Campaign/CreatorCampaignPage";
import PostDetailPage from "./Social/PostDetailPage";
import EngagementInbox from "./Social/EngagementInbox";
import SocialCalendarPage from "./Social/SocialCalendarPage";
import UsersGroupsPage from "./Social/UsersGroupsPage";
import MediaAssetsPage from "./Social/MediaAssetsPage";



function ElevationScroll(props) {
  const { children, window } = props;
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ? window() : undefined,
  });

  return cloneElement(children, {
    elevation: trigger ? 2 : 0,
  });
}

function LoadingScreen(props) {
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };
  const { loadingText } = props;
  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        backgroundColor: "rgba(0, 0, 0, 0.65)",
        position: "fixed",
        zIndex: 100000000,
        top: 0,
      }}
    >
      <div
        style={{
          margin: "auto",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          position: "absolute",
          textAlign: "center",
          display: "table",
        }}
      >
        <Lottie
          options={defaultOptions}
          style={{ opacity: 0.8, margin: "auto" }}
          height={300}
          width={300}
          isStopped={false}
          isPaused={false}
        />
        <Typography sx={{ color: "white", marginTop: -5 }} variant="subtitle1">
          <TypeAnimation sequence={[loadingText]} speed={55} cursor={true}>
            {" "}
          </TypeAnimation>
        </Typography>
      </div>
    </div>
  );
}

function ScreenSizeAlert(props) {
  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        backgroundColor: "rgba(0, 0, 0, 1)",
        position: "fixed",
        zIndex: 100000000,
        top: 0,
      }}
    >
      <div
        style={{
          margin: "auto",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          position: "absolute",
          textAlign: "center",
          display: "table",
        }}
      >
        <Typography sx={{ color: "white", marginTop: -5 }} variant="subtitle1">
          Your screen size is too small! For an optimal experience please view
          this page on your computer.
        </Typography>
      </div>
    </div>
  );
}

const THEME = createTheme({
  typography: {
    fontFamily: `Inter`,
    fontSize: 13,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    fontWeightSemiBold: 600,
    button: {
      textTransform: 'none'
    },
    h2: {
      fontSize: 30,
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "120%",
      letterSpacing: "-1px",
      color: "var(--main-text-color)",
    },
    h3: {
      fontSize: 24,
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "140%",
      letterSpacing: "-1px",
      color: "var(--main-text-color)",
    },
    h4: {
      fontSize: 20,
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "140%",
      letterSpacing: "-0.5px",
      color: "var(--main-text-color)",
    },
    h5: {
      fontSize: 18,
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "140%", /* 25.2px */
      letterSpacing: "-0.5px",
      color: "var(--main-text-color)",
    },
    bodyreg: {
      fontSize: 16,
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "140%",
      letterSpacing: "-0.3px",
    },
    bodyregm: {
      fontSize: 16,
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "140%",
      letterSpacing: "-0.3px",
    },
    bodysm: {
      fontSize: 14,
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "140%",
      letterSpacing: "-0.3px",
    },
    bodysmm: {
      fontSize: 14,
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "140%",
      letterSpacing: "-0.3px",
    },
    bodyxs: {
      fontSize: 12,
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "140%",
      letterSpacing: "-0.3px",
    }
  },
  palette: {
    primary: {
      main: "#1E22FB", //600 shade from design colour palette
      light: "#7375FD", //400
      dark: "#0306BA",//800
    },
    secondary: {
      main: "#8C1ED9", //600 new color not in design palette, added in app.css
      light: "#D9BFFF", //100
      dark: "#6610A0", //800
    },
    info: {
      main: "#475467", // neutrals from colour palette
      light: "#F2F4F7", // 100
      dark: "#344054",
    },
    success: {
      main: "#039855",
      light: "#D1FADF", // 100
      dark: "#05603A",
    },
    warning: {
      main: "#D97706",
      light: "#FBBF24",
      dark: "#92400E",
    },
    error: {
      main: "#DC2626",
      light: "#F87171",
      dark: "#7F1D1D",
    }
  },
});

const ProtectedRoute = ({ redirectPath = "/login", children }) => {
  const { user, isInit } = useContext(UserContext);
  const location = useLocation();
  
  // Wait for auth to initialize before making any routing decisions
  // This prevents redirects during the auth loading phase
  if (!isInit) {
    console.log("ProtectedRoute: waiting for auth init, current path:", location.pathname);
    return <div style={{ height: "100vh", width: "100vw" }}></div>;
  }
  
  // Use user.uid directly instead of authed state to avoid race condition
  // The authed state is set in a separate useEffect which can cause a brief
  // moment where isInit is true but authed hasn't been updated yet
  const isAuthenticated = !!user.uid;
  
  console.log("ProtectedRoute AFTER INIT", {
    path: location.pathname,
    isAuthenticated,
    userType: user.type,
    isSetup: user.isSetup,
    plan: user.plan,
    userId: user.id
  });
  
  if (!isAuthenticated) {
    console.log("REDIRECTING to login, reason: not authenticated")
    // Redirect creators to creator login instead of brand login
    const loginPath = user.type === "creator" ? "/creatorlogin" : redirectPath;
    return <Navigate to={loginPath} replace />;
  } else if (isAuthenticated && !user.plan && user.type !== "creator") {
    console.log("REDIRECTING to setup, reason: brand without plan")
    return <Navigate to={"/setup"} replace />;
  } else if (isAuthenticated && !user.isSetup && user.type === "creator") {
    console.log("REDIRECTING to creatorsetup, reason: creator not setup")
    // Redirect creators without setup to creator setup
    return <Navigate to={"/creatorsetup"} replace />;
  }
  console.log("RENDERING children for path:", location.pathname)
  return children ? <ScrollToTop><Layout>{children}</Layout></ScrollToTop> : <Outlet />;
};

const SetupRoute = ({ redirectPath = "/login", children }) => {
  const { user, isInit } = useContext(UserContext);
  
  // Wait for auth to initialize before making any routing decisions
  if (!isInit) {
    return <div style={{ height: "100vh", width: "100vw" }}></div>;
  }
  
  // Use user.uid directly instead of authed state to avoid race condition
  const isAuthenticated = !!user.uid;
  
  console.log("setup route", isAuthenticated)
  if (!isAuthenticated) {
    return <Navigate to={redirectPath} replace />;
  } else if (isAuthenticated && user.plan) {
    return <Navigate to={user.type === "creator"? "/creators" : "/campaigns"} replace />;
  } else if (isAuthenticated && user.type === "creator" && user.isSetup) {
    // Redirect creators who have completed setup
    return <Navigate to={"/creators"} replace />;
  }

  return children ? <ScrollToTop>{children}</ScrollToTop> : <Outlet />;
};

function App() {
  const router = createBrowserRouter([
    {
      path: "/",
      element: <ScrollToTop><Outlet /></ScrollToTop>,
      errorElement: <RouterErrorFallback />,
      children: [
        { index: true, element: <Home /> },
        { path: "login", element: <Login /> },
        { path: "register", element: <Register /> },
        {
          path: "setup",
          element: (
            <SetupRoute>
              <Setup />
            </SetupRoute>
          ),
        },
        { path: "admin/scraper", element: <ProtectedRoute><Scraper /></ProtectedRoute> },
        {
          path: "campaigns/:campaignId/workstream",
          element: (
            <ProtectedRoute>
              <Workstream />
            </ProtectedRoute>
          ),
        },
        {
          path: "campaigns/:campaignId",
          element: (
            <ProtectedRoute>
              <NewCampaignPage />
            </ProtectedRoute>
          ),
        },
        {
          path: "outreach/:creatorId",
          element: (
            <ProtectedRoute>
              <LearnMoreOutreach />
            </ProtectedRoute>
          ),
        },
        {
          path: "campaigns/FAQ",
          element: (
            <ProtectedRoute>
              <FrequentlyAskedQuestions />
            </ProtectedRoute>
          ),
        },
        {
          path: "campaigns/analyticsdashboard",
          element: (
            <ProtectedRoute>
              <AnalyticsDashboard />
            </ProtectedRoute>
          ),
        },
        {
          path: "campaigns/socialtrends",
          element: (
            <ProtectedRoute>
              <SocialTrends />
            </ProtectedRoute>
          ),
        },
        {
          path: "campaigns/creatordiscovery",
          element: (
            <ProtectedRoute>
              <CreatorDiscovery />
            </ProtectedRoute>
          ),
        },
        {
          path: "campaigns/account",
          element: (
            <ProtectedRoute>
              <MyAccount />
            </ProtectedRoute>
          ),
        },
        {
          path: "campaigns",
          element: (
            <ProtectedRoute>
              <Campaigns />
            </ProtectedRoute>
          ),
        },
        {
          path: "dashboard",
          element: (
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          ),
        },
        { path: "product/:productId", element: <ProductRedirect /> },
        { path: "pricing", element: <Pricing /> },
        { path: "creatorsignup", element: <CreatorSignUp /> },
        { path: "creatorlogin", element: <CreatorLogin /> },
        {
          path: "creators",
          element: (
            <ProtectedRoute>
              <Creators />
            </ProtectedRoute>
          ),
        },
        {
          path: "creators/:campaignId",
          element: (
            <ProtectedRoute>
              <CreatorCampaignPage />
            </ProtectedRoute>
          ),
        },
        {
          path: "creators/:campaignId/workstream",
          element: (
            <ProtectedRoute>
              <CreatorsWorkstream />
            </ProtectedRoute>
          ),
        },
        {
          path: "creatorsetup",
          element: (
            <SetupRoute redirectPath="/creatorlogin">
              <CreatorSetup />
            </SetupRoute>
          ),
        },
        {
          path: "creators/account",
          element: (
            <ProtectedRoute>
              <CreatorMyAccount />
            </ProtectedRoute>
          ),
        },
        { path: "privacy", element: <Privacy /> },
        { path: "terms", element: <Terms /> },
        { path: "limited-use", element: <LimitedUse /> },
        { path: "blog", element: <FAQ /> },
        { path: "blog/2025-influencer-marketing-insights", element: <BlogArticle5 /> },
        { path: "blog/blogarticle", element: <BlogArticle /> },
        { path: "blog/blogarticle3", element: <BlogArticle3 /> },
        { path: "blog/blogarticle4", element: <BlogArticle4 /> },
        { path: "blog/blogarticle2", element: <BlogArticle2 /> },
        { path: "privacypolicy", element: <PrivacyPolicy /> },
        { path: "delete-account", element: <AccountDeletion /> },
        { path: "oauth/youtube", element: <YoutubeOAuth /> },
        { path: "oauth/facebook", element: <FacebookOauth /> },
        { path: "oauth", element: <TiktokOauth /> },
        { path: "oauth/tiktok", element: <TiktokOauth /> },
        { path: "mailing/unsubscribe", element: <Unsubscribe /> },
        { path: "analytics/update", element: <TempAddAnalytics /> },
        {
          path: "competitive/landscape",
          element: (
            <ProtectedRoute>
              <Landscape />
            </ProtectedRoute>
          ),
        },
        { path: "match/:campaignId", element: <AutoInviteLogin /> },
        { path: "match/register/:campaignId", element: <AutoInviteRegister /> },
        {
          path: "favorites",
          element: (
            <ProtectedRoute>
              <Favorites />
            </ProtectedRoute>
          ),
        },
        {
          path: "campaigns/create",
          element: (
            <ProtectedRoute>
              <CreateCampaign />
            </ProtectedRoute>
          ),
        },
        {
          path: "campaigns/:campaignId/edit",
          element: (
            <ProtectedRoute>
              <CreateCampaign />
            </ProtectedRoute>
          ),
        },
        {
          path: "social/inbox",
          element: localStorage.getItem("socialToolEnabled") === "true" ? (
            <ProtectedRoute><EngagementInbox /></ProtectedRoute>
          ) : <Navigate to="/dashboard" replace />,
        },
        {
          path: "social/calendar",
          element: localStorage.getItem("socialToolEnabled") === "true" ? (
            <ProtectedRoute><SocialCalendarPage /></ProtectedRoute>
          ) : <Navigate to="/dashboard" replace />,
        },
        {
          path: "social/users",
          element: localStorage.getItem("socialToolEnabled") === "true" ? (
            <ProtectedRoute><UsersGroupsPage /></ProtectedRoute>
          ) : <Navigate to="/dashboard" replace />,
        },
        {
          path: "social/media",
          element: localStorage.getItem("socialToolEnabled") === "true" ? (
            <ProtectedRoute><MediaAssetsPage /></ProtectedRoute>
          ) : <Navigate to="/dashboard" replace />,
        },
        {
          path: "social/posts/:postId",
          element: localStorage.getItem("socialToolEnabled") === "true" ? (
            <ProtectedRoute><PostDetailPage /></ProtectedRoute>
          ) : <Navigate to="/dashboard" replace />,
        },
      ],
    },
  ]);
  const { isLoading, loadingText } = useContext(LoadingContext);
  const isDesktop = useMediaQuery("(min-width:1000px)");

  
  return (
    <div>
      {isLoading && <LoadingScreen loadingText={loadingText} />}
      {!isDesktop &&
        !window.location.pathname.startsWith("/oauth") &&
        window.location.pathname !== "/" &&
        window.location.pathname !== "/pricing" &&
        window.location.pathname !== "/login" &&
        window.location.pathname !== "/register" &&
        window.location.pathname !== "/blog" &&
        window.location.pathname !== "/creatorSetup" &&
        // window.location.pathname !== "/creatorhome" &&
        window.location.pathname !== "/setup" &&
        window.location.pathname !== "/mailing/unsubscribe" &&
        window.location.pathname !== "/setup" &&
        window.location.pathname !== "/blog/blogarticle" &&
        window.location.pathname !== "/setup" &&
        window.location.pathname !== "/blog/blogarticle3" &&
        window.location.pathname !== "/setup" &&
        window.location.pathname !== "/blog/blogarticle4" &&
        window.location.pathname !== "/setup" &&
        window.location.pathname !== "/blog/blogarticle2" && 
        window.location.pathname !== "blog/2025-influencer-marketing-insights" && 
        window.location.pathname !== "/privacypolicy" && 
        window.location.pathname !== "/delete-account" &&
        window.location.pathname !== "/setup" &&
        window.location.pathname !== "/creatorSignUp" && 
        window.location.pathname !== "/creatorsignup" &&  (
          <ScreenSizeAlert />
        )}

      <ThemeProvider theme={THEME}>
        <CssBaseline />
          <div>
            <RouterProvider router={router} />
          </div>
      </ThemeProvider>
    </div>
  );
}

export default App;
